/** biome-ignore-all lint/suspicious/noExplicitAny: ok> */
/** biome-ignore-all lint/correctness/noUndeclaredVariables: ok */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';

// Universal Place Prediction interface
export interface PlacePrediction {
  placeId: string;
  text: string;
  description: string;
}

// New Places API price level enum
export enum PlacePriceLevel {
  FREE = 'PRICE_LEVEL_FREE',
  INEXPENSIVE = 'PRICE_LEVEL_INEXPENSIVE',
  MODERATE = 'PRICE_LEVEL_MODERATE',
  EXPENSIVE = 'PRICE_LEVEL_EXPENSIVE',
  VERY_EXPENSIVE = 'PRICE_LEVEL_VERY_EXPENSIVE',
  UNSPECIFIED = 'PRICE_LEVEL_UNSPECIFIED',
}

export function convertPriceLevelToNumber(
  priceLevel?: PlacePriceLevel | string
): number | undefined {
  if (!priceLevel) {
    return undefined;
  }

  const priceLevelMap: { [key in PlacePriceLevel]: number | undefined } = {
    [PlacePriceLevel.FREE]: 0,
    [PlacePriceLevel.INEXPENSIVE]: 1,
    [PlacePriceLevel.MODERATE]: 2,
    [PlacePriceLevel.EXPENSIVE]: 3,
    [PlacePriceLevel.VERY_EXPENSIVE]: 4,
    [PlacePriceLevel.UNSPECIFIED]: undefined,
  };

  return priceLevelMap[priceLevel as PlacePriceLevel];
}

interface PlaceDetailsNew {
  id: string;
  displayName?: { text: string };
  formattedAddress?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  regularOpeningHours?: {
    weekdayDescriptions?: string[];
  };
  businessStatus?: string;
  types?: string[];
  primaryTypeDisplayName?: { text: string; languageCode?: string };
  rating?: number;
  userRatingCount?: number;
  websiteUri?: string;
  nationalPhoneNumber?: string;
  internationalPhoneNumber?: string;
  addressComponents?: Array<{
    longText: string;
    shortText: string;
    types: string[];
  }>;
  priceLevel?: string;
  googleMapsUri?: string;
  googleMapsLinks?: {
    reviewsUri?: string;
    directionsUri?: string;
  };
  adrFormatAddress?: string;
  photos?: Array<{
    name: string;
    heightPx: number;
    widthPx: number;
    authorAttributions?: Array<{
      displayName?: string;
      uri?: string;
      photoUri?: string;
    }>;
  }>;
}

/**
 * Convert New Places API response to the legacy PlaceResult shape used throughout the app
 */
function parseNewApiPlace(newDetails: PlaceDetailsNew): google.maps.places.PlaceResult {
  const legacyDetails: google.maps.places.PlaceResult = {
    place_id: newDetails.id,
    name: newDetails.displayName?.text,
    formatted_address: newDetails.formattedAddress,
    business_status: newDetails.businessStatus as google.maps.places.BusinessStatus,
    types: newDetails.types || [],
    rating: newDetails.rating,
    user_ratings_total: newDetails.userRatingCount,
    website: newDetails.websiteUri,
    url: newDetails.googleMapsLinks?.reviewsUri || newDetails.googleMapsUri,
    formatted_phone_number: newDetails.nationalPhoneNumber,
    international_phone_number: newDetails.internationalPhoneNumber,
    price_level: convertPriceLevelToNumber(newDetails.priceLevel),
  };

  if (newDetails.primaryTypeDisplayName?.text) {
    (legacyDetails as any).primaryTypeDisplayName = newDetails.primaryTypeDisplayName.text;
  }

  // Convert location
  if (newDetails.location) {
    legacyDetails.geometry = {
      location: {
        lat: () => newDetails.location?.latitude || 0,
        lng: () => newDetails.location?.longitude || 0,
      } as google.maps.LatLng,
    } as google.maps.places.PlaceGeometry;
  }

  // Convert opening hours
  if (newDetails.regularOpeningHours?.weekdayDescriptions) {
    legacyDetails.opening_hours = {
      weekday_text: newDetails.regularOpeningHours.weekdayDescriptions,
    } as google.maps.places.PlaceOpeningHours;
  }

  // Convert address components
  if (newDetails.addressComponents) {
    legacyDetails.address_components = newDetails.addressComponents.map((component) => ({
      long_name: component.longText,
      short_name: component.shortText,
      types: component.types,
    }));
  }

  // Convert ADR address
  if (newDetails.adrFormatAddress) {
    legacyDetails.adr_address = newDetails.adrFormatAddress;
  }

  // Convert photos with error handling
  if (newDetails.photos && newDetails.photos.length > 0) {
    legacyDetails.photos = newDetails.photos.map(
      (photo) =>
        ({
          photo_reference: photo.name,
          height: photo.heightPx,
          width: photo.widthPx,
          html_attributions:
            photo.authorAttributions?.map(
              (attr) => `<a href="${attr.uri || ''}">${attr.displayName || 'Attribution'}</a>`
            ) || [],
          // Deliberately not implemented — photos are no longer resolved to a URL
          // client-side. Use photo_reference with resolvePlacePhotoUrl() instead.
          getUrl: () => {
            throw new Error(
              'photo.getUrl() is no longer supported — use photo_reference with resolvePlacePhotoUrl().'
            );
          },
        }) as google.maps.places.PlacePhoto
    );
  }

  return legacyDetails;
}

// In-session cache for Places API responses

interface CacheEntry {
  data: google.maps.places.PlaceResult;
  timestamp: number;
  fields: string[];
}

// Plain JSON-safe shape we write to sessionStorage (no functions, no class instances)
interface SerializedPlaceData {
  place_id?: string;
  name?: string;
  formatted_address?: string;
  business_status?: string;
  types?: string[];
  rating?: number;
  user_ratings_total?: number;
  website?: string;
  url?: string;
  formatted_phone_number?: string;
  international_phone_number?: string;
  price_level?: number;
  adr_address?: string;
  weekdayText?: string[];
  photoReferences?: string[];
  primaryTypeDisplayName?: string;
  location?: { lat: number; lng: number };
  addressComponents?: Array<{ long_name: string; short_name: string; types: string[] }>;
}

interface SerializedEntry {
  data: SerializedPlaceData;
  timestamp: number;
  fields: string[];
}

const placeDetailsCache = new Map<string, CacheEntry>();

const CACHE_DURATIONS = {
  placeDetails: 4 * 60 * 60 * 1000, // 4 hours
  photos: 24 * 60 * 60 * 1000, // 24 hours
  reviews: 2 * 60 * 60 * 1000, // 2 hours
};

const MAX_CACHE_SIZE = 100;
const STORAGE_KEY = 'wander_place_cache_v2';

restoreCache();

function getCacheKey(placeId: string, fields: string[]): string {
  return `${placeId}_${fields.sort().join(',')}`;
}

function resolveCacheDuration(fields: string[]): number {
  const fieldSet = new Set(fields.map((f) => f.toLowerCase()));
  if (fieldSet.has('photos')) {
    return CACHE_DURATIONS.photos;
  }
  if (fieldSet.has('reviews') || fieldSet.has('rating') || fieldSet.has('user_ratings_total')) {
    return CACHE_DURATIONS.reviews;
  }
  return CACHE_DURATIONS.placeDetails;
}

function readFromCache(placeId: string, fields: string[]): google.maps.places.PlaceResult | null {
  const key = getCacheKey(placeId, fields);
  const entry = placeDetailsCache.get(key);
  const age = Date.now() - (entry?.timestamp || 0);

  if (entry && age < resolveCacheDuration(fields)) {
    return entry.data;
  }

  if (entry) {
    placeDetailsCache.delete(key);
    saveCache();
  }

  return null;
}

export function invalidatePlacePhotoCache(placeId: string): void {
  const key = getCacheKey(placeId, ['photos']);
  if (placeDetailsCache.has(key)) {
    placeDetailsCache.delete(key);
    saveCache();
  }
}

function writeToCache(
  placeId: string,
  fields: string[],
  data: google.maps.places.PlaceResult
): void {
  const key = getCacheKey(placeId, fields);
  placeDetailsCache.set(key, { data, timestamp: Date.now(), fields });

  if (placeDetailsCache.size > MAX_CACHE_SIZE) {
    const oldestKey = placeDetailsCache.keys().next().value;
    if (oldestKey) {
      placeDetailsCache.delete(oldestKey);
    }
  }

  saveCache();
}

// ─── sessionStorage persistence ───────────────────────────────────────────
// PlaceResult contains functions (photo.getUrl, geometry.location.lat) that
// can't be JSON-stringified, so we convert to/from a plain object for storage.

function serializeEntry(entry: CacheEntry): SerializedEntry {
  const { data, timestamp, fields } = entry;

  const serialized: SerializedPlaceData = {
    place_id: data.place_id,
    name: data.name,
    formatted_address: data.formatted_address,
    business_status: data.business_status,
    types: data.types,
    rating: data.rating,
    user_ratings_total: data.user_ratings_total,
    website: data.website,
    url: data.url,
    formatted_phone_number: data.formatted_phone_number,
    international_phone_number: data.international_phone_number,
    price_level: data.price_level,
    adr_address: data.adr_address,
    weekdayText: data.opening_hours?.weekday_text,
    addressComponents: data.address_components?.map((c) => ({
      long_name: c.long_name,
      short_name: c.short_name,
      types: c.types,
    })),
    primaryTypeDisplayName: (data as any).primaryTypeDisplayName,
  };

  const location = data.geometry?.location;
  if (location) {
    serialized.location = { lat: location.lat(), lng: location.lng() };
  }

  if (data.photos?.length) {
    serialized.photoReferences = data.photos
      .map((photo) => (photo as any).photo_reference)
      .filter((ref): ref is string => Boolean(ref));
  }

  return { data: serialized, timestamp, fields };
}

function deserializeEntry(serialized: SerializedEntry): CacheEntry {
  const { data, timestamp, fields } = serialized;

  const placeResult: google.maps.places.PlaceResult = {
    place_id: data.place_id,
    name: data.name,
    formatted_address: data.formatted_address,
    business_status: data.business_status as google.maps.places.BusinessStatus | undefined,
    types: data.types,
    rating: data.rating,
    user_ratings_total: data.user_ratings_total,
    website: data.website,
    url: data.url,
    formatted_phone_number: data.formatted_phone_number,
    international_phone_number: data.international_phone_number,
    price_level: data.price_level,
    adr_address: data.adr_address,
  };

  if (data.photoReferences?.length) {
    placeResult.photos = data.photoReferences.map((ref) => ({
      photo_reference: ref,
      height: 0,
      width: 0,
      html_attributions: [],
      getUrl: () => {
        throw new Error(
          'photo.getUrl() is no longer supported — use photo_reference with resolvePlacePhotoUrl().'
        );
      },
    })) as google.maps.places.PlacePhoto[];
  }

  if (data.weekdayText) {
    placeResult.opening_hours = {
      weekday_text: data.weekdayText,
    } as google.maps.places.PlaceOpeningHours;
  }

  if (data.addressComponents) {
    placeResult.address_components = data.addressComponents;
  }

  if (data.location) {
    const { lat, lng } = data.location;
    placeResult.geometry = {
      location: { lat: () => lat, lng: () => lng } as google.maps.LatLng,
    } as google.maps.places.PlaceGeometry;
  }

  if (data.primaryTypeDisplayName) {
    (placeResult as any).primaryTypeDisplayName = data.primaryTypeDisplayName;
  }

  return { data: placeResult, timestamp, fields };
}

function saveCache(): void {
  try {
    const out: Record<string, SerializedEntry> = {};
    for (const [key, entry] of Array.from(placeDetailsCache.entries())) {
      try {
        out[key] = serializeEntry(entry);
      } catch {
        // Skip any entry that can't be serialized
      }
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(out));
  } catch {
    // sessionStorage unavailable or quota exceeded — in-memory cache still works
  }
}

function restoreCache(): void {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as Record<string, SerializedEntry>;
    const now = Date.now();

    for (const [key, serialized] of Object.entries(parsed)) {
      if (now - serialized.timestamp < resolveCacheDuration(serialized.fields ?? [])) {
        try {
          placeDetailsCache.set(key, deserializeEntry(serialized));
        } catch {
          // Skip corrupted entries
        }
      }
    }
  } catch {
    // sessionStorage is corrupted — clear it and start fresh
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export async function getDetails(options: {
  placeId: string;
  fields?: string[];
  sessionToken?: string;
}): Promise<google.maps.places.PlaceResult> {
  const {
    placeId,
    fields = ['id', 'displayName', 'formattedAddress', 'location'],
    // Unused for now — the getPlaceDetails gateway doesn't yet accept a session
    // token to link back to an autocomplete session for Google's discounted billing.
    sessionToken: _sessionToken,
  } = options;

  const cached = readFromCache(placeId, fields);
  if (cached) {
    return cached;
  }

  try {
    const getPlaceDetailsCallable = httpsCallable<{ googlePlaceId: string }, PlaceDetailsNew>(
      functions,
      'getPlaceDetails'
    );
    const { data } = await getPlaceDetailsCallable({ googlePlaceId: placeId });
    const result = parseNewApiPlace(data);
    writeToCache(placeId, fields, result);
    return result;
  } catch (newApiError) {
    // Exclude photos (legacy tokens expire quickly and cause 403s) and any
    // New-Places-API-only field names the legacy widget doesn't recognize —
    // it errors out on the whole request rather than ignoring unknown fields.
    const NEW_API_ONLY_FIELDS = new Set(['photos', 'primaryTypeDisplayName']);
    const legacyFields = (fields as string[]).filter((f) => !NEW_API_ONLY_FIELDS.has(f));

    // A photos-only request (e.g. the card photo self-heal) has nothing left
    // to ask the legacy widget for — it can never serve photos anyway, so
    // don't send a request Google's SDK will reject for having no fields.
    if (legacyFields.length === 0) {
      throw new Error(`Places API unavailable: ${newApiError}`);
    }

    // Fall back to the legacy Maps JS SDK
    if (window.google?.maps?.places?.PlacesService) {
      return new Promise((resolve, reject) => {
        const service = new window.google.maps.places.PlacesService(document.createElement('div'));

        const request: google.maps.places.PlaceDetailsRequest = {
          placeId,
          fields: legacyFields as any,
        };

        service.getDetails(request, (result, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
            writeToCache(placeId, fields, result);
            resolve(result);
          } else {
            reject(new Error(`Legacy Places API error: ${status}`));
          }
        });
      });
    }

    throw new Error(`Places API unavailable: ${newApiError}`);
  }
}

export async function getGeocode(options: {
  placeId: string;
}): Promise<google.maps.GeocoderResult[]> {
  const details = await getDetails({ placeId: options.placeId, fields: ['location'] });

  if (!details.geometry?.location) {
    throw new Error('No location found for place');
  }

  return [
    {
      geometry: {
        location: details.geometry?.location || new google.maps.LatLng(0, 0),
        location_type: 'APPROXIMATE' as google.maps.GeocoderLocationType,
        viewport: details.geometry?.viewport || undefined,
      } as google.maps.GeocoderGeometry,
      address_components: [],
      formatted_address: '',
      place_id: options.placeId,
      types: [],
    },
  ];
}

export function getLatLng(result: google.maps.GeocoderResult): google.maps.LatLng {
  return new google.maps.LatLng(result.geometry.location.lat(), result.geometry.location.lng());
}

// Fields fetched when saving a place as a card — matches the autocomplete search field set.
export const FULL_CARD_FIELDS = [
  'address_components',
  'adr_address',
  'business_status',
  'formatted_address',
  'formatted_phone_number',
  'geometry.location',
  'international_phone_number',
  'name',
  'opening_hours.weekday_text',
  'photos',
  'price_level',
  'primaryTypeDisplayName',
  'rating',
  'url',
  'user_ratings_total',
  'website',
];

export interface PlaceCardFallback {
  placeId: string;
  name: string;
  lat: number;
  lng: number;
  rating?: number;
  photoReferences?: string[];
}

/**
 * Fetches full place details and returns a card-ready data object.
 * Used when adding a place from the map popup, producing the same
 * card shape as adding via the search autocomplete.
 */
export async function buildCardFromPlaceDetails(
  poi: PlaceCardFallback,
  options?: { sessionToken?: string }
) {
  const details = await getDetails({
    placeId: poi.placeId,
    fields: FULL_CARD_FIELDS,
    sessionToken: options?.sessionToken,
  });
  const photoReferences = extractPhotoReferences(details);
  return {
    googlePlaceId: poi.placeId,
    name: details.name ?? poi.name,
    addressComponents: details.address_components,
    adrAddress: details.adr_address,
    businessStatus: details.business_status,
    formattedAddress: details.formatted_address,
    formattedPhoneNumber: details.formatted_phone_number,
    internationalPhoneNumber: details.international_phone_number,
    location: details.geometry?.location
      ? { latitude: details.geometry.location.lat(), longitude: details.geometry.location.lng() }
      : { latitude: poi.lat, longitude: poi.lng },
    openingHours: details.opening_hours?.weekday_text,
    priceLevel:
      typeof details.price_level === 'string'
        ? convertPriceLevelToNumber(details.price_level as PlacePriceLevel)
        : details.price_level,
    rating: details.rating ?? poi.rating,
    userRatingsTotal: details.user_ratings_total,
    placeType: (details as any).primaryTypeDisplayName,
    url: details.url,
    website: details.website,
    ...(photoReferences.length > 0
      ? { photoReferences }
      : poi.photoReferences?.length
        ? { photoReferences: poi.photoReferences }
        : {}),
  };
}

const MAX_PHOTO_COUNT = 8;

/**
 * Extracts opaque photo references (not URLs — no key, nothing fetchable
 * client-side) for storage. Resolve them to renderable images at render time
 * via resolvePlacePhotoUrl(), which calls the getPlacePhoto gateway function.
 */
export function extractPhotoReferences(
  details: google.maps.places.PlaceResult,
  maxCount = MAX_PHOTO_COUNT
): string[] {
  if (!details.photos || details.photos.length === 0) {
    return [];
  }

  return details.photos
    .slice(0, maxCount)
    .map((photo) => (photo as any).photo_reference)
    .filter((ref): ref is string => Boolean(ref));
}
